import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getDisplayName,
  getNotificationPreview,
  getWhatsAppInboxNotifications,
  markAllWhatsAppInboxRead,
  markWhatsAppConversationRead,
  normalizeInboxNotifications,
} from "../helper/whatsapp_inbox_helper";

const POLL_INTERVAL_MS = 15000;
const WhatsAppNotificationContext = createContext(null);

const requestBrowserNotificationPermission = async () => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }

  if (Notification.permission === "granted" || Notification.permission === "denied") {
    return Notification.permission;
  }

  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
};

const showBrowserNotification = (item) => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return;
  }

  if (Notification.permission !== "granted" || !item) {
    return;
  }

  const title = `WhatsApp: ${getDisplayName(item.contactName, item.waId)}`;
  const body = getNotificationPreview(item.body, 120);

  try {
    const notification = new Notification(title, {
      body,
      tag: item._id ? `wa-${item._id}` : undefined,
      icon: "/favicon.ico",
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch {
    // Ignore browsers that block Notification construction.
  }
};

export function WhatsAppNotificationProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentUnread, setRecentUnread] = useState([]);
  const [loading, setLoading] = useState(false);

  const prevUnreadCountRef = useRef(0);
  const lastNotifiedIdRef = useRef(null);
  const isFirstPollRef = useRef(true);
  const pollingRef = useRef(false);

  const notifyNewMessage = useCallback(
    (item) => {
      if (!item) return;

      const name = getDisplayName(item.contactName, item.waId);
      const preview = getNotificationPreview(item.body);

      toast.info(
        <div>
          <strong>{name}</strong>
          <div className="small text-muted">{preview}</div>
        </div>,
        {
          onClick: () => navigate(`/whatsapp-inbox?waId=${encodeURIComponent(item.waId)}`),
        }
      );

      showBrowserNotification(item);
    },
    [navigate]
  );

  const refreshNotifications = useCallback(async () => {
    if (pollingRef.current) return;
    if (!localStorage.getItem("adminToken")) return;

    pollingRef.current = true;
    setLoading(true);

    try {
      const res = await getWhatsAppInboxNotifications({ limit: 10 });
      if (res?.success === false) {
        return;
      }

      const { unreadCount: nextCount, recentUnread: nextRecent } =
        normalizeInboxNotifications(res);
      const newest = nextRecent[0] || null;
      const newestId = newest?._id ? String(newest._id) : null;
      const onInboxPage = location.pathname === "/whatsapp-inbox";

      if (
        !isFirstPollRef.current &&
        !onInboxPage &&
        nextCount > prevUnreadCountRef.current &&
        newestId &&
        newestId !== lastNotifiedIdRef.current
      ) {
        notifyNewMessage(newest);
        lastNotifiedIdRef.current = newestId;
      }

      if (isFirstPollRef.current && newestId) {
        lastNotifiedIdRef.current = newestId;
      }

      isFirstPollRef.current = false;
      prevUnreadCountRef.current = nextCount;
      setUnreadCount(nextCount);
      setRecentUnread(nextRecent);
    } catch {
      // Silent fail — header should not spam errors on network issues.
    } finally {
      pollingRef.current = false;
      setLoading(false);
    }
  }, [location.pathname, notifyNewMessage]);

  const markConversationRead = useCallback(
    async (waId) => {
      if (!waId) return;

      try {
        const res = await markWhatsAppConversationRead(waId);
        if (res?.success === false) {
          return;
        }
        await refreshNotifications();
      } catch {
        // Endpoint may not exist on current API deploy (404). Inbox still works.
      }
    },
    [refreshNotifications]
  );

  const markAllRead = useCallback(async () => {
    const res = await markAllWhatsAppInboxRead();
    if (res?.success === false) {
      toast.error(res?.message || "Failed to mark notifications read");
      return;
    }

    prevUnreadCountRef.current = 0;
    setUnreadCount(0);
    setRecentUnread([]);
    toast.success("All WhatsApp notifications marked read");
  }, []);

  useEffect(() => {
    requestBrowserNotificationPermission();
  }, []);

  useEffect(() => {
    refreshNotifications();

    const timer = setInterval(() => {
      if (document.hidden) return;
      refreshNotifications();
    }, POLL_INTERVAL_MS);

    const handleVisibility = () => {
      if (!document.hidden) {
        refreshNotifications();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [refreshNotifications]);

  const value = useMemo(
    () => ({
      unreadCount,
      recentUnread,
      loading,
      refreshNotifications,
      markConversationRead,
      markAllRead,
    }),
    [
      unreadCount,
      recentUnread,
      loading,
      refreshNotifications,
      markConversationRead,
      markAllRead,
    ]
  );

  return (
    <WhatsAppNotificationContext.Provider value={value}>
      {children}
    </WhatsAppNotificationContext.Provider>
  );
}

export const useWhatsAppNotifications = () => {
  const context = useContext(WhatsAppNotificationContext);
  if (!context) {
    throw new Error("useWhatsAppNotifications must be used within WhatsAppNotificationProvider");
  }
  return context;
};
